import { useMemo, useState } from 'react';
import type { DashboardChartSlice } from '../../types/dashboard';
import { DashboardBreakdownTooltip, sliceBreakdownLines } from './DashboardBreakdownTooltip';

const CHART_SIZE = 160;
const CX = CHART_SIZE / 2;
const CY = CHART_SIZE / 2;
const OUTER_R = 80;
const INNER_R = 44;
const TAU = Math.PI * 2;
const START_ANGLE = -Math.PI / 2;

function polar(cx: number, cy: number, r: number, angle: number) {
  return { x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) };
}

function donutSegmentPath(
  cx: number,
  cy: number,
  outerR: number,
  innerR: number,
  start: number,
  end: number,
): string {
  const sweep = end - start;
  if (sweep <= 0) return '';

  if (sweep >= TAU - 0.0001) {
    const mid = start + Math.PI;
    const first = donutSegmentPath(cx, cy, outerR, innerR, start, mid);
    const second = donutSegmentPath(cx, cy, outerR, innerR, mid, end);
    return `${first} ${second}`.trim();
  }

  const large = sweep > Math.PI ? 1 : 0;
  const outerStart = polar(cx, cy, outerR, start);
  const outerEnd = polar(cx, cy, outerR, end);
  const innerEnd = polar(cx, cy, innerR, end);
  const innerStart = polar(cx, cy, innerR, start);

  return [
    `M ${outerStart.x} ${outerStart.y}`,
    `A ${outerR} ${outerR} 0 ${large} 1 ${outerEnd.x} ${outerEnd.y}`,
    `L ${innerEnd.x} ${innerEnd.y}`,
    `A ${innerR} ${innerR} 0 ${large} 0 ${innerStart.x} ${innerStart.y}`,
    'Z',
  ].join(' ');
};

type Segment = {
  slice: DashboardChartSlice;
  startAngle: number;
  endAngle: number;
  path: string;
};

function buildSegments(slices: DashboardChartSlice[], total: number): Segment[] {
  if (total <= 0) return [];

  let cursor = START_ANGLE;
  return slices.map((slice) => {
    const sweep = (slice.value / total) * TAU;
    const startAngle = cursor;
    const endAngle = cursor + sweep;
    cursor = endAngle;
    return {
      slice,
      startAngle,
      endAngle,
      path: donutSegmentPath(CX, CY, OUTER_R, INNER_R, startAngle, endAngle),
    };
  });
}

export function DashboardDoughnutChart({ slices }: { slices: DashboardChartSlice[] }) {
  const total = useMemo(() => slices.reduce((sum, s) => sum + s.value, 0), [slices]);
  const segments = useMemo(() => buildSegments(slices, total), [slices, total]);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [tipPos, setTipPos] = useState<{ x: number; y: number } | null>(null);

  const hoveredSlice = hoveredIndex !== null ? slices[hoveredIndex] : null;
  const tipLines = hoveredSlice ? sliceBreakdownLines(hoveredSlice, total) : [];

  return (
    <div className="dash-doughnut-wrap">
      <svg
        className="dash-doughnut"
        width={CHART_SIZE}
        height={CHART_SIZE}
        viewBox={`0 0 ${CHART_SIZE} ${CHART_SIZE}`}
        role="img"
        aria-label="Doughnut chart"
      >
        {total <= 0 ? (
          <circle cx={CX} cy={CY} r={OUTER_R} fill="var(--bl)" />
        ) : (
          segments.map((segment, idx) => (
            <path
              key={`${segment.slice.label}-${idx}`}
              d={segment.path}
              fill={segment.slice.color}
              className={hoveredIndex === idx ? 'dash-doughnut__segment is-hovered' : 'dash-doughnut__segment'}
              onMouseEnter={(event) => {
                setHoveredIndex(idx);
                setTipPos({ x: event.clientX, y: event.clientY });
              }}
              onMouseMove={(event) => {
                setTipPos({ x: event.clientX, y: event.clientY });
              }}
              onMouseLeave={() => {
                setHoveredIndex(null);
                setTipPos(null);
              }}
            >
              <title>{segment.slice.label}</title>
            </path>
          ))
        )}
        <circle cx={CX} cy={CY} r={INNER_R} fill="var(--surface)" pointerEvents="none" />
      </svg>

      <DashboardBreakdownTooltip lines={tipLines} anchor={tipPos} />
    </div>
  );
}

export function DashboardChartLegend({ slices }: { slices: DashboardChartSlice[] }) {
  const total = slices.reduce((sum, s) => sum + s.value, 0);
  const [tooltipIndex, setTooltipIndex] = useState<number | null>(null);
  const [tipPos, setTipPos] = useState<{ x: number; y: number } | null>(null);

  const activeSlice = tooltipIndex !== null ? slices[tooltipIndex] : null;
  const tipLines = activeSlice ? sliceBreakdownLines(activeSlice, total) : [];

  return (
    <>
      <ul className="dash-legend" role="list">
        {slices.map((slice, idx) => (
          <li
            key={slice.label}
            className="dash-legend__item"
            onMouseEnter={(event) => {
              setTooltipIndex(idx);
              setTipPos({ x: event.clientX, y: event.clientY });
            }}
            onMouseMove={(event) => {
              setTipPos({ x: event.clientX, y: event.clientY });
            }}
            onMouseLeave={() => {
              setTooltipIndex(null);
              setTipPos(null);
            }}
          >
            <span className="dash-legend__dot" style={{ background: slice.color }} />
            <span>{slice.label}</span>
            <span className="dash-legend__pct">
              {total > 0 ? Math.round((slice.value / total) * 100) : 0}% ({slice.value.toLocaleString()})
            </span>
          </li>
        ))}
      </ul>

      <DashboardBreakdownTooltip lines={tipLines} anchor={tipPos} className="dash-legend__breakdown" />
    </>
  );
}
